import { useUpdateCommissionMutation } from "api/TenantSlug/Commission/hooks";
import { ComposedValue } from "components/UI/Table/UI/InteractiveCell/components/WithPayoutFormula";
import { toast } from "react-toastify";

import CommissionData = App.Data.CommissionData;
import UpdateCommissionRequest = App.Http.Requests.Commission.UpdateCommissionRequest;

type UpdateCommissionMutationFn = ReturnType<typeof useUpdateCommissionMutation>["mutateAsync"];

interface UpdateCommissionOptions {
    commissionId: string;
    value: ComposedValue;
    updateCommissionMutation: UpdateCommissionMutationFn;
    onSuccess?: () => void;
    successMessage?: string;
    errorMessage?: string;
}

/**
 * Builds the update request from the ComposedValue
 */
export const buildCommissionUpdateRequest = (value: ComposedValue): UpdateCommissionRequest => {
    // If isEnabled is defined, we're handling the disabled state
    const updateRequest: UpdateCommissionRequest =
        value.isEnabled !== undefined ? { is_enabled: value.isEnabled } : {};

    // Only update formula fields if not disabled and values are provided
    if (value.isEnabled !== false && value.formulaType && value.payoutFormula) {
        updateRequest.payout_formula = value.payoutFormula;
        updateRequest.formula_type = value.formulaType as CommissionData["formula_type"];
    }

    return updateRequest;
};

/**
 * Generic function to update a commission
 */
export const updateCommission = async ({
    commissionId,
    value,
    updateCommissionMutation,
    onSuccess,
    successMessage,
    errorMessage,
}: UpdateCommissionOptions) => {
    const updateRequest = buildCommissionUpdateRequest(value);

    // If no fields to update, return early
    if (Object.keys(updateRequest).length === 0) return Promise.resolve();

    await updateCommissionMutation(
        { commissionId, request: updateRequest },
        {
            onSuccess: () => {
                if (successMessage) {
                    toast.success(successMessage);
                }
                onSuccess?.();
            },
            onError: () => {
                if (errorMessage) {
                    toast.error(errorMessage);
                }
            },
        }
    );
};

/**
 * Helper function to update a child commission
 */
export const updateChildCommission = async (
    value: ComposedValue,
    record: CommissionData,
    childId: string,
    updateCommissionMutation: UpdateCommissionMutationFn,
    options?: {
        onSuccess?: () => void;
        successMessage?: string;
        errorMessage?: string;
        notFoundMessage?: string;
    }
) => {
    if (!record.children) {
        toast.error(options?.notFoundMessage ?? "Commission not found");
        return Promise.reject(new Error("Child commission not found"));
    }

    const child = record.children.find((child: CommissionData) => child.id === childId);

    if (!child) {
        toast.error(options?.notFoundMessage ?? "Commission not found");
        return Promise.reject(new Error("Child commission not found"));
    }

    await updateCommission({
        commissionId: child.id,
        value,
        updateCommissionMutation,
        ...options,
    });
};

/**
 * Helper function to update a parent commission
 */
export const updateParentCommission = async (
    value: ComposedValue,
    record: CommissionData,
    updateCommissionMutation: UpdateCommissionMutationFn,
    options?: {
        onSuccess?: () => void;
        successMessage?: string;
        errorMessage?: string;
    }
) => {
    await updateCommission({
        commissionId: record.id,
        value,
        updateCommissionMutation,
        ...options,
    });
};